/**
 * @fileoverview Simple DOM Render Helpers
 * Minimal utilities for rendering view models to the DOM.
 * @module shared/components/simpleRender
 *
 * CONTRACT:
 * - el() creates DOM elements with attributes and children
 * - render* functions transform view model data into DOM elements
 * - All functions are pure (no side effects beyond DOM creation)
 * - CSS classes follow cap-* naming convention for future styling
 *
 * CSS CLASS CONVENTION:
 * - cap-root: Root container
 * - cap-shell: Main shell/layout wrapper
 * - cap-headline-metrics: Metrics grid container
 * - cap-metric-card: Individual metric card
 * - cap-panel: Generic panel container
 * - cap-panel-title: Panel title
 * - cap-panel-description: Panel description
 * - cap-narrative: Narrative text container
 * - cap-debug: Debug section container
 */

/**
 * Creates an HTML element with optional attributes and children.
 *
 * @param {string} tag - HTML tag name
 * @param {Object} [attrs={}] - Attributes to set (supports className, style, on* events)
 * @param {(string|Node)[]} [children=[]] - Child nodes or text content
 * @returns {HTMLElement} The created element
 *
 * @example
 * el("div", { className: "cap-panel" }, [
 *   el("h2", {}, ["Title"]),
 *   "Some text content"
 * ]);
 */
export function el(tag, attrs = {}, children = []) {
  const element = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (key === "className") {
      element.className = value;
    } else if (key === "style" && typeof value === "string") {
      element.setAttribute("style", value);
    } else if (key.startsWith("on") && typeof value === "function") {
      element.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (value !== undefined && value !== null) {
      element.setAttribute(key, String(value));
    }
  }

  for (const child of children) {
    if (typeof child === "string") {
      element.appendChild(document.createTextNode(child));
    } else if (child instanceof Node) {
      element.appendChild(child);
    }
  }

  return element;
}

/**
 * Renders a single headline metric card.
 *
 * @param {import('../../observatories/dollar_liquidity/types.js').HeadlineMetric} metric - Metric data
 * @returns {HTMLElement} Rendered metric card
 */
export function renderMetricCard(metric) {
  const severityClass = metric.severity ? `severity-${metric.severity}` : "";
  const directionIndicator =
    metric.direction === "up"
      ? "↑"
      : metric.direction === "down"
      ? "↓"
      : "";

  return el("div", { className: `cap-metric-card metric-card ${severityClass}` }, [
    el("div", { className: "cap-metric-label metric-label" }, [metric.label]),
    el("div", { className: "cap-metric-value metric-value" }, [
      directionIndicator ? `${directionIndicator} ` : "",
      typeof metric.value === "number"
        ? metric.value.toFixed(2)
        : String(metric.value),
      el("span", { className: "cap-metric-unit metric-unit" }, [metric.unit || ""]),
    ]),
  ]);
}

/**
 * Renders the headline metrics grid.
 *
 * @param {import('../../observatories/dollar_liquidity/types.js').HeadlineMetric[]} headlineMetrics - Array of metrics
 * @returns {HTMLElement} Rendered metrics grid
 */
export function renderHeadlineMetrics(headlineMetrics) {
  return el(
    "div",
    { className: "cap-headline-metrics metrics-grid" },
    headlineMetrics.map((metric) => renderMetricCard(metric))
  );
}

/**
 * Renders a timeseries panel (chart placeholder + metadata).
 *
 * @param {import('../../observatories/dollar_liquidity/types.js').Panel} panel - Panel data
 * @returns {HTMLElement} Rendered timeseries panel
 */
export function renderTimeseriesPanel(panel) {
  const { series, metadata } = panel.payload;

  // For now, render a simple text representation
  // Future: integrate with d3 or other charting
  const firstPoint = series?.[0];
  const lastPoint = series?.[series?.length - 1];

  const summaryText = firstPoint && lastPoint
    ? `${series.length} data points from ${firstPoint.t} to ${lastPoint.t}`
    : "No data available";

  return el("div", { className: "cap-panel cap-panel-timeseries" }, [
    el("div", { className: "cap-panel-title" }, [panel.title]),
    panel.description
      ? el("div", { className: "cap-panel-description" }, [panel.description])
      : null,
    el("div", { className: "cap-chart-placeholder", style: "padding: 1rem; background: #0d0d12; border-radius: 4px; margin-top: 0.5rem;" }, [
      el("div", { style: "color: #666; font-size: 0.875rem;" }, [
        `📈 Chart: ${summaryText}`,
      ]),
      metadata
        ? el("div", { style: "color: #555; font-size: 0.75rem; margin-top: 0.5rem;" }, [
            `Metadata: ${JSON.stringify(metadata)}`,
          ])
        : null,
    ]),
  ].filter(Boolean));
}

/**
 * Renders a summary/text panel.
 *
 * @param {import('../../observatories/dollar_liquidity/types.js').Panel} panel - Panel data
 * @returns {HTMLElement} Rendered summary panel
 */
export function renderSummaryPanel(panel) {
  const { text, metadata } = panel.payload;

  return el("div", { className: "cap-panel cap-panel-summary" }, [
    el("div", { className: "cap-panel-title" }, [panel.title]),
    panel.description
      ? el("div", { className: "cap-panel-description" }, [panel.description])
      : null,
    el("div", { className: "cap-narrative" }, [text || "No content"]),
    metadata
      ? el("div", { style: "color: #555; font-size: 0.75rem; margin-top: 0.5rem;" }, [
          `Metadata: ${JSON.stringify(metadata)}`,
        ])
      : null,
  ].filter(Boolean));
}

/**
 * Renders a narrative panel (styled text block).
 *
 * @param {import('../../observatories/dollar_liquidity/types.js').Panel} panel - Panel data
 * @returns {HTMLElement} Rendered narrative panel
 */
export function renderNarrativePanel(panel) {
  const { text } = panel.payload;

  return el("div", { className: "cap-panel cap-panel-narrative narrative" }, [
    el("div", { className: "cap-panel-title narrative-title" }, [panel.title]),
    el("div", { className: "cap-narrative" }, [text || "No content"]),
  ]);
}

/**
 * Renders a panel based on its type.
 * Dispatches to the appropriate panel renderer.
 *
 * @param {import('../../observatories/dollar_liquidity/types.js').Panel} panel - Panel data
 * @returns {HTMLElement} Rendered panel
 */
export function renderPanel(panel) {
  switch (panel.type) {
    case "timeseries":
      return renderTimeseriesPanel(panel);
    case "text":
      return renderSummaryPanel(panel);
    case "metric":
      // Could add specific metric panel rendering here
      return renderSummaryPanel(panel);
    default:
      // Fallback for unknown panel types
      return el("div", { className: "cap-panel" }, [
        el("div", { className: "cap-panel-title" }, [panel.title]),
        el("div", { className: "cap-panel-description" }, [
          `Unknown panel type: ${panel.type}`,
        ]),
      ]);
  }
}

/**
 * Renders the debug section with full view model JSON.
 *
 * @param {import('../../observatories/dollar_liquidity/types.js').DollarLiquidityViewModel} viewModel - View model data
 * @returns {HTMLElement} Rendered debug section
 */
export function renderDebugSection(viewModel) {
  return el("div", { className: "cap-debug debug-section" }, [
    el("div", { className: "cap-debug-title debug-title" }, [
      "Full View Model (Debug)",
    ]),
    el("pre", { className: "cap-debug-json debug-json" }, [
      JSON.stringify(viewModel, null, 2),
    ]),
  ]);
}

/**
 * Renders the complete Dollar Liquidity dashboard.
 * Composes headline metrics, panels, and debug section.
 *
 * @param {import('../../observatories/dollar_liquidity/types.js').DollarLiquidityViewModel} viewModel - View model data
 * @returns {HTMLElement} Rendered dashboard
 */
export function renderDashboard(viewModel) {
  const container = el("div", { className: "cap-root" }, []);

  // Header
  container.appendChild(el("h1", {}, ["Dollar Liquidity Observatory"]));
  container.appendChild(
    el("p", { className: "subtitle" }, [
      `Time Range: ${viewModel.timeRange} • Last updated: ${new Date().toISOString().split("T")[0]}`,
    ])
  );

  // Headline Metrics
  container.appendChild(renderHeadlineMetrics(viewModel.headlineMetrics));

  // Narrative (if present at top level)
  if (viewModel.narrative) {
    container.appendChild(
      el("div", { className: "cap-panel cap-panel-narrative narrative" }, [
        el("div", { className: "cap-panel-title narrative-title" }, [
          "Current Conditions",
        ]),
        el("div", { className: "cap-narrative" }, [viewModel.narrative]),
      ])
    );
  }

  // Panels
  for (const panel of viewModel.panels) {
    container.appendChild(renderPanel(panel));
  }

  // Debug section
  container.appendChild(renderDebugSection(viewModel));

  return container;
}
